import React, { useState, useContext, useEffect } from 'react';
import InboundContext from './InboundContext';
import moment from 'moment';

const SendEmailInbound = ({ recipientEmail, onClose, sendWithInboundInfo = true}) => {
  const { selectedNickname, inboundInfo } = useContext(InboundContext);

  // Define a default message when there is no inboundInfo
  const defaultMessage = `We have received items that we cannot locate an inbound for.
A brief description of the items/orders received is as follows:

Staff Comment:
Time of Comment: ${moment().format('ddd, MMM Do [at] h:mm A')}
Employee Name: ${selectedNickname}

Please provide any information requested, or process any adjustments or changes as needed ASAP and let us know.

Kind Regards,
YYZ Prep Team`;

  // If inboundInfo is available and sendWithInboundInfo is true, generate the usual message
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (inboundInfo && sendWithInboundInfo) {
      setMessage(`Staff Comment:

Time of Comment: ${moment().format('ddd, MMM Do [at] h:mm A')}
Employee Name: ${selectedNickname}
Inbound Name: ${inboundInfo.name}
Inbound Link: https://app.yyzprep.ca/shipments/inbound/${inboundInfo.id}

Please provide any information requested, or process any adjustments or changes as needed ASAP and let us know.

Send us an email, text, or call to notify us of any changes that may be necessary. Due to the volume of shipments being processed, and depending on your shipment type, it may be adjusted to ship out to improve lead times.

Kind Regards,
YYZ Prep Team`);
    } else {
      // Set default message if inboundInfo is not available or if sendWithInboundInfo is false
      setMessage(defaultMessage);
    }
  }, [inboundInfo, selectedNickname, sendWithInboundInfo]);

  const sendEmail = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientEmail: recipientEmail,
          subject: `[ACTION REQUIRED] YYZ Prep | ${inboundInfo && sendWithInboundInfo ? 'Inbound Shipment Issue' : 'Unknown Inbound'}`,
          text: message,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      alert('Email sent successfully');
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email');
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-[600px] max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between border-b pb-4 mb-4">
          <h2 className="text-xl font-semibold text-gray-800">New Email</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-4 flex-1 overflow-hidden">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span className="font-medium w-12">To:</span>
            <span className="flex-1 px-3 py-2 bg-gray-50 rounded">{recipientEmail}</span>
          </div>

          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span className="font-medium w-12">Subject:</span>
            <span className="flex-1 px-3 py-2 bg-gray-50 rounded">
              [ACTION REQUIRED] YYZ Prep | {inboundInfo && sendWithInboundInfo ? 'Inbound Shipment Issue' : 'Unknown Inbound'}
            </span>
          </div>

          <div className="flex-1 overflow-hidden flex flex-col">
            <label className="text-sm font-medium text-gray-600 mb-2">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="flex-1 w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
              style={{ minHeight: '300px' }}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-4 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={sendEmail}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Send Email
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendEmailInbound;
